"use client";

import type React from "react";
import { useState, useEffect, useCallback } from "react";
import {
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Box,
  FormControl,
  FormLabel,
  Text,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  VStack,
  useToast,
  HStack,
  Flex,
  Spinner,
} from "@chakra-ui/react";
import { useAppTheme } from "../../../ThemeContext.tsx";
import { VoicemailSettings } from "./VoicemailSettings.tsx";
import { agentService } from "../../../services/agentService.ts";

interface CallSettingsProps {
  agentId: string;
  initialSettings?: {
    enable_voicemail_detection?: boolean;
    end_call_after_silence_ms?: number;
    max_call_duration_ms?: number;
    begin_message_delay_ms?: number;
    voicemail_message?: string;
    voicemail_detection_timeout_ms?: number;
  };
}

export const CallSettings: React.FC<CallSettingsProps> = ({
  agentId,
  initialSettings,
}) => {
  const [settings, setSettings] = useState(initialSettings || {});
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const toast = useToast();
  const { resolvedTheme } = useAppTheme();
  const isDark = resolvedTheme === "dark";

  // Fetch the latest agent settings when the component mounts
  useEffect(() => {
    const fetchAgentSettings = async () => {
      setIsLoading(true);
      try {
        const data = await agentService.getAgentDetails(agentId);
        setSettings({
          enable_voicemail_detection: data.enable_voicemail_detection,
          end_call_after_silence_ms: data.end_call_after_silence_ms,
          max_call_duration_ms: data.max_call_duration_ms,
          begin_message_delay_ms: data.begin_message_delay_ms,
          voicemail_message: data.voicemail_message,
          voicemail_detection_timeout_ms: data.voicemail_detection_timeout_ms,
        });
      } catch (error) {
        console.error("Error fetching agent settings:", error);
        toast({
          title: "Error fetching agent settings",
          status: "error",
          duration: 3000,
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchAgentSettings();
  }, [agentId, toast]);

  // Format milliseconds to appropriate unit (seconds, minutes, or hours)
  const formatTime = (ms: number) => {
    if (ms < 60000) {
      // Less than 1 minute
      return `${(ms / 1000).toFixed(1)} s`;
    } else if (ms < 3600000) {
      // Less than 1 hour
      return `${(ms / 60000).toFixed(1)} m`;
    } else {
      return `${(ms / 3600000).toFixed(2)} h`;
    }
  };

  const updateAgentSettings = useCallback(
    async (newSettings: Partial<typeof settings>) => {
      setIsUpdating(true);
      try {
        await agentService.updateAgentSettings(agentId, newSettings);

        setSettings((prevSettings) => ({ ...prevSettings, ...newSettings }));
        toast({
          title: "Call settings updated",
          status: "success",
          duration: 2000,
        });
      } catch (error: any) {
        console.error("Error updating call settings:", error);
        toast({
          title: "Error updating call settings",
          description:
            error.response?.data?.message || "An unexpected error occurred",
          status: "error",
          duration: 3000,
        });
      } finally {
        setIsUpdating(false);
      }
    },
    [agentId, toast]
  );

  const handleEndCallAfterSilenceChange = useCallback(
    async (value: number) => {
      await updateAgentSettings({ end_call_after_silence_ms: value });
    },
    [updateAgentSettings]
  );

  const handleMaxCallDurationChange = useCallback(
    async (value: number) => {
      await updateAgentSettings({ max_call_duration_ms: value });
    },
    [updateAgentSettings]
  );

  const handleBeginMessageDelayChange = useCallback(
    async (value: number) => {
      await updateAgentSettings({ begin_message_delay_ms: value });
    },
    [updateAgentSettings]
  );

  if (isLoading) {
    return (
      <Flex h="100%" w="100%" justify="center" align="center">
        <Spinner size="md" />
      </Flex>
    );
  }

  return (
    <Accordion allowToggle>
      <AccordionItem border="none">
        <h2>
          <AccordionButton
            py={3}
            px={4}
            _hover={{ bg: isDark ? "gray.800" : "gray.200" }}
          >
            <Box
              flex="1"
              textAlign="left"
              fontSize="sm"
              display="flex"
              alignItems="center"
              gap={2}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
              >
                <path
                  d="M10 4C8.4087 4 6.88258 4.63214 5.75736 5.75736C4.63214 6.88258 4 8.4087 4 10H6.25C6.64782 10 7.02936 10.158 7.31066 10.4393C7.59196 10.7206 7.75 11.1022 7.75 11.5V15.25C7.75 15.6478 7.59196 16.0294 7.31066 16.3107C7.02936 16.592 6.64782 16.75 6.25 16.75H4C3.60218 16.75 3.22064 16.592 2.93934 16.3107C2.65804 16.0294 2.5 15.6478 2.5 15.25V10C2.5 5.85775 5.85775 2.5 10 2.5C14.1423 2.5 17.5 5.85775 17.5 10V15.25C17.5 15.6478 17.342 16.0294 17.0607 16.3107C16.7794 16.592 16.3978 16.75 16 16.75H13.75C13.3522 16.75 12.9706 16.592 12.6893 16.3107C12.408 16.0294 12.25 15.6478 12.25 15.25V11.5C12.25 11.1022 12.408 10.7206 12.6893 10.4393C12.9706 10.158 13.3522 10 13.75 10H16C16 8.4087 15.3679 6.88258 14.2426 5.75736C13.1174 4.63214 11.5913 4 10 4ZM4 11.5V15.25H6.25V11.5H4ZM13.75 11.5V15.25H16V11.5H13.75Z"
                  fill="currentColor"
                ></path>
              </svg>
              Call Settings
            </Box>
            <AccordionIcon />
          </AccordionButton>
        </h2>
        <AccordionPanel pb={4} bg={isDark ? "gray.800" : "gray.100"}>
          <VStack spacing={6} align="stretch">
            <VoicemailSettings
              agentId={agentId}
              initialSettings={{
                enable_voicemail_detection: settings.enable_voicemail_detection,
                voicemail_message: settings.voicemail_message,
                voicemail_detection_timeout_ms:
                  settings.voicemail_detection_timeout_ms,
              }}
            />
            {/* End Call on Silence */}
            <FormControl>
              <FormLabel
                fontSize="sm"
                fontWeight="medium"
                color={isDark ? "gray.300" : "gray.700"}
                mb={1}
              >
                End Call on Silence
              </FormLabel>
              <HStack justify="space-between" mb={2}>
                <Slider
                  aria-label="End call after silence"
                  min={10000} // 10 seconds
                  max={1800000} // 30 minutes
                  step={10000} // 10 seconds
                  value={settings.end_call_after_silence_ms}
                  onChange={(value) =>
                    setSettings((prev) => ({
                      ...prev,
                      end_call_after_silence_ms: value,
                    }))
                  }
                  onChangeEnd={handleEndCallAfterSilenceChange}
                  isDisabled={isUpdating}
                  width="80%"
                >
                  <SliderTrack bg={isDark ? "gray.600" : "gray.200"}>
                    <SliderFilledTrack bg="gray.500" />
                  </SliderTrack>
                  <SliderThumb boxSize={4} />
                </Slider>
                <Text
                  fontSize="sm"
                  fontWeight="medium"
                  color={isDark ? "gray.300" : "gray.700"}
                >
                  {formatTime(settings.end_call_after_silence_ms || 0)}
                </Text>
              </HStack>
            </FormControl>

            {/* Max Call Duration */}
            <FormControl>
              <FormLabel
                fontSize="sm"
                fontWeight="medium"
                color={isDark ? "gray.300" : "gray.700"}
                mb={1}
              >
                Max Call Duration
              </FormLabel>
              <HStack justify="space-between" mb={2}>
                <Slider
                  aria-label="Max call duration"
                  min={60000} // 1 minute
                  max={7200000} // 2 hours
                  step={60000} // 1 minute
                  value={settings.max_call_duration_ms}
                  onChange={(value) =>
                    setSettings((prev) => ({
                      ...prev,
                      max_call_duration_ms: value,
                    }))
                  }
                  onChangeEnd={handleMaxCallDurationChange}
                  isDisabled={isUpdating}
                  width="80%"
                >
                  <SliderTrack bg={isDark ? "gray.600" : "gray.200"}>
                    <SliderFilledTrack bg="gray.500" />
                  </SliderTrack>
                  <SliderThumb boxSize={4} />
                </Slider>
                <Text
                  fontSize="sm"
                  fontWeight="medium"
                  color={isDark ? "gray.300" : "gray.700"}
                >
                  {formatTime(settings.max_call_duration_ms || 0)}
                </Text>
              </HStack>
            </FormControl>

            {/* Pause Before Speaking */}
            <FormControl>
              <FormLabel
                fontSize="sm"
                fontWeight="medium"
                color={isDark ? "gray.300" : "gray.700"}
                mb={1}
              >
                Pause Before Speaking
              </FormLabel>
              <Text
                fontSize="xs"
                color={isDark ? "gray.400" : "gray.600"}
                mb={2}
              >
                The duration before the assistant starts speaking at the
                beginning of the call.
              </Text>
              <HStack justify="space-between" mb={2}>
                <Slider
                  aria-label="Begin message delay"
                  min={0}
                  max={5000} // 5 seconds
                  step={100} // 0.1 seconds
                  value={settings.begin_message_delay_ms}
                  onChange={(value) =>
                    setSettings((prev) => ({
                      ...prev,
                      begin_message_delay_ms: value,
                    }))
                  }
                  onChangeEnd={handleBeginMessageDelayChange}
                  isDisabled={isUpdating}
                  width="80%"
                >
                  <SliderTrack bg={isDark ? "gray.600" : "gray.200"}>
                    <SliderFilledTrack bg="gray.500" />
                  </SliderTrack>
                  <SliderThumb boxSize={4} />
                </Slider>
                <Text
                  fontSize="sm"
                  fontWeight="medium"
                  color={isDark ? "gray.300" : "gray.700"}
                >
                  {formatTime(settings.begin_message_delay_ms || 0)}
                </Text>
              </HStack>
            </FormControl>
          </VStack>
        </AccordionPanel>
      </AccordionItem>
    </Accordion>
  );
};
